import { supabase } from "../supabaseClient";
import { logger } from "../utils/logger";

const REQUEST_ROW_BUILDERS = {
  join: ({ fromUserId, groupId }) => ({ from_user_id: fromUserId, request_type: "join", group_id: groupId, status: "pending" }),
  leave: ({ fromUserId, groupId }) => ({ from_user_id: fromUserId, request_type: "leave", group_id: groupId, status: "pending" }),
  create: ({ fromUserId, newGroupName }) => ({ from_user_id: fromUserId, request_type: "create", new_group_name: newGroupName, status: "pending" }),
  leader_change: ({ fromUserId, groupId, targetUserId }) => ({ from_user_id: fromUserId, request_type: "leader_change", group_id: groupId, target_user_id: targetUserId, status: "pending" }),
  bulk_invite: ({ fromUserId, groupId, invitedEmails }) => ({ from_user_id: fromUserId, request_type: "bulk_invite", group_id: groupId, invited_emails: invitedEmails, status: "pending" }),
  feedback: ({ fromUserId, feedbackMessage }) => ({ from_user_id: fromUserId, request_type: "feedback", feedback_message: feedbackMessage, status: "pending" }),
};

export async function listRequests({ userId, isAdmin }) {
  let query = supabase.from("group_requests").select("*, groups(name)").order("created_at", { ascending: false });
  if (!isAdmin) {
    query = query.eq("from_user_id", userId);
  }
  const { data, error } = await query;

  if (error) {
    logger.error('groupRequestsService', 'list_requests_failed', { error: error.message });
    throw error;
  }
  return data || [];
}

export async function submitRequest({ fromUserId, requestType, groupId, newGroupName, targetUserId, invitedEmails, feedbackMessage }) {
  const row = REQUEST_ROW_BUILDERS[requestType]({ fromUserId, groupId, newGroupName, targetUserId, invitedEmails, feedbackMessage });
  const { data, error } = await supabase.from("group_requests").insert(row).select("id").single();

  if (error) {
    logger.error('groupRequestsService', 'request_submit_failed', { requestType, error: error.message });
    throw error;
  }

  if (requestType === "feedback") {
    logger.info('groupRequestsService', 'feedback_submitted', { requestId: data.id });
  } else {
    const detail = { requestType, groupId: groupId ?? null };
    if (requestType === "create") detail.newGroupName = newGroupName;
    if (requestType === "leader_change") detail.targetUserId = targetUserId;
    if (requestType === "bulk_invite") detail.emailCount = invitedEmails?.length || 0;
    logger.info('groupRequestsService', 'request_submitted', detail);
  }

  const { error: notifyError } = await supabase.functions.invoke("notify-admin-request", { body: { requestId: data.id } });
  if (notifyError) {
    logger.warn('groupRequestsService', 'admin_notify_failed', { requestId: data.id, error: notifyError.message });
  }

  return data;
}

export async function recordAdminAdd({ userId, groupId, resolvedBy }) {
  const { error } = await supabase.from("group_requests").insert({
    from_user_id: userId,
    request_type: "admin_add",
    group_id: groupId,
    status: "approved",
    resolved_by: resolvedBy,
    resolved_at: new Date().toISOString(),
    read_by_requester: false,
  });

  if (error) {
    logger.error('groupRequestsService', 'admin_add_record_failed', { userId, groupId, error: error.message });
    throw error;
  }
  logger.info('groupRequestsService', 'admin_add_recorded', { userId, groupId, resolvedBy });
}

export async function recordAdminRemove({ userId, groupId, resolvedBy }) {
  const { error } = await supabase.from("group_requests").insert({
    from_user_id: userId,
    request_type: "admin_remove",
    group_id: groupId,
    status: "approved",
    resolved_by: resolvedBy,
    resolved_at: new Date().toISOString(),
    read_by_requester: false,
  });

  if (error) {
    logger.error('groupRequestsService', 'admin_remove_record_failed', { userId, groupId, error: error.message });
    throw error;
  }
  logger.info('groupRequestsService', 'admin_remove_recorded', { userId, groupId, resolvedBy });
}

export function resolveRecipientIds({ selection, allMemberships }) {
  if (selection.allUsers) return selection.allUserIds;
  const ids = new Set(selection.userIds);
  allMemberships
    .filter((m) => selection.groupIds.includes(m.group_id))
    .forEach((m) => ids.add(m.user_id));
  return [...ids];
}

export async function sendAdminMessage({ recipientIds, messageText, sentBy }) {
  if (!recipientIds || recipientIds.length === 0) return { batchId: null, recipientCount: 0 };

  const batchId = crypto.randomUUID();
  const now = new Date().toISOString();
  const { error } = await supabase.from("group_requests").insert(
    recipientIds.map((userId) => ({
      from_user_id: userId,
      request_type: "admin_message",
      status: "approved",
      resolved_by: sentBy,
      resolved_at: now,
      admin_response: messageText,
      batch_id: batchId,
      read_by_requester: false,
    }))
  );

  if (error) {
    logger.error('groupRequestsService', 'admin_message_send_failed', { recipientCount: recipientIds.length, error: error.message });
    throw error;
  }
  logger.info('groupRequestsService', 'admin_message_sent', { batchId, recipientCount: recipientIds.length, sentBy });
  return { batchId, recipientCount: recipientIds.length };
}

export async function recordLeaderVacancy({ groupIds }) {
  if (!groupIds || groupIds.length === 0) return;

  const { error } = await supabase.from("group_requests").insert(
    groupIds.map((groupId) => ({
      from_user_id: null,
      request_type: "leader_vacancy",
      group_id: groupId,
      target_user_id: null,
      status: "pending",
    }))
  );

  if (error) {
    logger.error('groupRequestsService', 'leader_vacancy_record_failed', { groupIds, error: error.message });
    throw error;
  }
  logger.info('groupRequestsService', 'leader_vacancy_recorded', { groupIds });
}

export async function approveRequest({ requestId, resolvedBy, requestType, fromUserId }) {
  const { error } = await supabase.from("group_requests").update({
    status: "approved",
    resolved_at: new Date().toISOString(),
    resolved_by: resolvedBy,
  }).eq("id", requestId);

  if (error) {
    logger.error('groupRequestsService', 'request_approve_failed', { requestId, error: error.message });
    throw error;
  }
  logger.info('groupRequestsService', 'request_approved', { requestType, fromUserId });
}

export async function rejectRequest({ requestId, resolvedBy, requestType, fromUserId }) {
  const { error } = await supabase.from("group_requests").update({
    status: "rejected",
    resolved_at: new Date().toISOString(),
    resolved_by: resolvedBy,
  }).eq("id", requestId);

  if (error) {
    logger.error('groupRequestsService', 'request_reject_failed', { requestId, error: error.message });
    throw error;
  }
  logger.info('groupRequestsService', 'request_rejected', { requestType, fromUserId });
}

export async function receiveFeedback({ requestId, resolvedBy, replyText }) {
  const trimmedReply = (replyText || "").trim();
  const { error } = await supabase.from("group_requests").update({
    status: "received",
    resolved_at: new Date().toISOString(),
    resolved_by: resolvedBy,
    admin_response: trimmedReply || null,
  }).eq("id", requestId);

  if (error) {
    logger.error('groupRequestsService', 'feedback_receive_failed', { requestId, error: error.message });
    throw error;
  }
  logger.info('groupRequestsService', 'feedback_received', { requestId, hasCustomReply: !!trimmedReply });
}

export async function addFeedbackToTodo({ requestId, todoItemText, addedBy }) {
  const trimmedText = (todoItemText || "").trim();
  const { error } = await supabase.from("group_requests").update({
    todo_item_text: trimmedText,
  }).eq("id", requestId);

  if (error) {
    logger.error('groupRequestsService', 'feedback_add_to_todo_failed', { requestId, error: error.message });
    throw error;
  }
  logger.info('groupRequestsService', 'feedback_added_to_todo', { requestId, addedBy });
}

export async function assignVacancyLeader({ requestId, resolvedBy, groupId, targetUserId }) {
  const { error } = await supabase.from("group_requests").update({
    status: "approved",
    resolved_at: new Date().toISOString(),
    resolved_by: resolvedBy,
  }).eq("id", requestId);

  if (error) {
    logger.error('groupRequestsService', 'vacancy_assign_failed', { requestId, error: error.message });
    throw error;
  }
  logger.info('groupRequestsService', 'vacancy_resolved', { groupId, userId: targetUserId });
}

export async function markRequestsRead({ userId }) {
  const { error } = await supabase.from("group_requests")
    .update({ read_by_requester: true })
    .eq("from_user_id", userId)
    .neq("status", "pending");

  if (error) {
    logger.error('groupRequestsService', 'mark_read_failed', { userId, error: error.message });
    throw error;
  }
  logger.info('groupRequestsService', 'requests_marked_read', { userId });
}

export async function hasPendingGroupRequest({ userId }) {
  const { data, error } = await supabase
    .from("group_requests")
    .select("id")
    .eq("from_user_id", userId)
    .in("request_type", ["join", "create"])
    .eq("status", "pending")
    .limit(1);

  if (error) {
    logger.error('groupRequestsService', 'pending_group_request_check_failed', { userId, error: error.message });
    throw error;
  }
  return (data?.length || 0) > 0;
}

export async function getUnreadCount({ userId, isAdmin }) {
  if (isAdmin) {
    const { count, error } = await supabase
      .from("group_requests")
      .select("id", { count: "exact", head: true })
      .eq("status", "pending");
    if (error) {
      logger.error('groupRequestsService', 'unread_count_failed', { userId, isAdmin, error: error.message });
      throw error;
    }
    return count || 0;
  }

  const { count, error } = await supabase
    .from("group_requests")
    .select("id", { count: "exact", head: true })
    .eq("from_user_id", userId)
    .neq("status", "pending")
    .eq("read_by_requester", false);

  if (error) {
    logger.error('groupRequestsService', 'unread_count_failed', { userId, isAdmin, error: error.message });
    throw error;
  }
  return count || 0;
}

export async function getNotifySettings() {
  const { data, error } = await supabase
    .from("admin_notification_settings")
    .select("notify_admin_user_id")
    .eq("id", 1)
    .maybeSingle();

  if (error) {
    logger.error('groupRequestsService', 'get_notify_settings_failed', { error: error.message });
    throw error;
  }
  return data || null;
}

export async function setNotifySettings({ notifyAdminUserId }) {
  const { error } = await supabase
    .from("admin_notification_settings")
    .upsert({ id: 1, notify_admin_user_id: notifyAdminUserId || null }, { onConflict: "id" });

  if (error) {
    logger.error('groupRequestsService', 'set_notify_settings_failed', { error: error.message });
    throw error;
  }
  logger.info('groupRequestsService', 'notify_target_changed', { notifyAdminUserId: notifyAdminUserId || null });
}

export function subscribeToRequestChanges({ userId, isAdmin }, onChange) {
  const config = { event: "*", schema: "public", table: "group_requests" };
  if (!isAdmin) {
    config.filter = `from_user_id=eq.${userId}`;
  }
  const channel = supabase
    .channel(`group-requests-${userId}`)
    .on("postgres_changes", config, onChange)
    .subscribe();

  return () => supabase.removeChannel(channel);
}
